import QRCode from 'qrcode';

export interface DynamicQRCustomization {
  size: number;
  foregroundColor: string;
  backgroundColor: string;
  errorCorrectionLevel: 'L' | 'M' | 'Q' | 'H';
  transparentBackground: boolean;
  logoFile?: File | null;
  logoDataUrl?: string | null;
  margin?: number;
}

export interface GeneratedQRResult {
  pngDataUrl: string;
  svgString: string;
  customization: DynamicQRCustomization;
}

/**
 * Generate a customized QR code with logo overlay
 */
export const generateCustomizedDynamicQR = async (
  content: string,
  customization: DynamicQRCustomization
): Promise<GeneratedQRResult> => {
  try {
    // First generate the base QR code as SVG
    const svgString = await generateQRCodeSVG(content, customization);
    
    // Then create PNG with logo overlay if needed
    const pngDataUrl = await generateQRCodePNG(content, customization);
    
    return {
      pngDataUrl,
      svgString,
      customization
    };
  } catch (error) {
    console.error('Error generating customized QR code:', error);
    throw new Error('Failed to generate QR code with customizations');
  }
};

/**
 * Generate QR code as SVG string
 */
const generateQRCodeSVG = async (
  content: string,
  customization: DynamicQRCustomization
): Promise<string> => {
  try {
    const options = {
      errorCorrectionLevel: customization.errorCorrectionLevel,
      type: 'svg' as const,
      quality: 0.92,
      margin: customization.margin || 4,
      color: {
        dark: customization.foregroundColor,
        light: customization.transparentBackground ? 'transparent' : customization.backgroundColor
      },
      width: customization.size
    };

    const svgString = await QRCode.toString(content, options);
    return svgString as string;
  } catch (error) {
    throw new Error(`Failed to generate SVG: ${error}`);
  }
};

/**
 * Generate QR code as PNG with logo overlay
 */
const generateQRCodePNG = async (
  content: string,
  customization: DynamicQRCustomization
): Promise<string> => {
  try {
    const options = {
      errorCorrectionLevel: customization.errorCorrectionLevel,
      type: 'image/png' as const,
      quality: 0.92,
      margin: customization.margin || 4,
      color: {
        dark: customization.foregroundColor,
        light: customization.transparentBackground ? 'transparent' : customization.backgroundColor
      },
      width: customization.size
    };

    // Generate base QR code as data URL
    const qrDataUrl = await QRCode.toDataURL(content, options);

    // If no logo, return the QR code directly
    if (!customization.logoDataUrl) {
      return qrDataUrl;
    }

    // If logo is provided, overlay it on a canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Canvas context not available');
    }

    canvas.width = customization.size;
    canvas.height = customization.size;

    // Load the QR code image
    const qrImg = new Image();
    qrImg.crossOrigin = 'anonymous';

    return new Promise((resolve, reject) => {
      qrImg.onload = () => {
        try {
          // Clear canvas with background
          if (!customization.transparentBackground) {
            ctx.fillStyle = customization.backgroundColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }

          // Draw QR code
          ctx.drawImage(qrImg, 0, 0, customization.size, customization.size);

          // Add logo overlay
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';

          logoImg.onload = () => {
            try {
              addLogoToCanvas(ctx, customization.logoDataUrl!, customization.size)
                .then(() => {
                  resolve(canvas.toDataURL('image/png'));
                })
                .catch(reject);
            } catch (error) {
              reject(error);
            }
          };

          logoImg.onerror = () => {
            reject(new Error('Failed to load logo image'));
          };

          logoImg.src = customization.logoDataUrl!;
        } catch (error) {
          reject(error);
        }
      };

      qrImg.onerror = () => {
        reject(new Error('Failed to load QR code image'));
      };

      qrImg.src = qrDataUrl;
    });
  } catch (error) {
    throw new Error(`Failed to generate PNG: ${error}`);
  }
};

/**
 * Add logo overlay to canvas
 */
const addLogoToCanvas = (
  ctx: CanvasRenderingContext2D,
  logoDataUrl: string,
  qrSize: number
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const logoImg = new Image();
    
    logoImg.onload = () => {
      try {
        // Calculate logo size (max 20% of QR code size)
        const maxLogoSize = qrSize * 0.2;
        const logoAspectRatio = logoImg.width / logoImg.height;
        
        let logoWidth, logoHeight;
        if (logoAspectRatio > 1) {
          logoWidth = Math.min(maxLogoSize, logoImg.width);
          logoHeight = logoWidth / logoAspectRatio;
        } else {
          logoHeight = Math.min(maxLogoSize, logoImg.height);
          logoWidth = logoHeight * logoAspectRatio;
        }

        // Center the logo
        const logoX = (qrSize - logoWidth) / 2;
        const logoY = (qrSize - logoHeight) / 2;

        // Add white background circle for logo
        const logoRadius = Math.max(logoWidth, logoHeight) / 2 + 8;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(qrSize / 2, qrSize / 2, logoRadius, 0, 2 * Math.PI);
        ctx.fill();

        // Draw logo
        ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
        
        resolve();
      } catch (error) {
        reject(error);
      }
    };

    logoImg.onerror = () => {
      reject(new Error('Failed to load logo image'));
    };

    logoImg.src = logoDataUrl;
  });
};

/**
 * Convert file to data URL
 */
export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      if (e.target?.result) {
        resolve(e.target.result as string);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Validate image file
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  // Check file size (2MB limit)
  if (file.size > 2 * 1024 * 1024) {
    return { valid: false, error: 'File size must be less than 2MB' };
  }

  // Check file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'File must be an image (JPEG, PNG, GIF, or WebP)' };
  }

  return { valid: true };
};

/**
 * Download QR code as PNG
 */
export const downloadDynamicQRCodePNG = (
  dataUrl: string,
  filename: string = 'dynamic-qr-code'
) => {
  const link = document.createElement('a');
  link.download = `${filename}.png`;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Download QR code as SVG
 */
export const downloadDynamicQRCodeSVG = (
  svgString: string,
  filename: string = 'dynamic-qr-code'
) => {
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.download = `${filename}.svg`;
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};
